import { useState } from 'react';
import { Gauge } from '../../components/Gauge';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { useHeldResource } from '../../lib/hooks';

interface Asset {
  id: number;
  name: string;
  type: string | null;
  category: string | null;
  product: string | null;
  serialNumber: string | null;
  warrantyExpires: string | null;
  active: boolean;
}

interface Contract {
  id: number;
  name: string;
  number: string | null;
  type: string | null;
  end: string | null;
}

export type Kind = 'server' | 'contract' | 'firewall' | 'vmware';

export interface Item {
  key: string;
  kind: Kind;
  name: string;
  detail: string | null;
  date: string | null;
}

export const KINDS: { kind: Kind; label: string; noun: string }[] = [
  { kind: 'server', label: 'Server warranties', noun: 'server' },
  { kind: 'contract', label: 'Contract renewals', noun: 'contract' },
  { kind: 'firewall', label: 'Firewall renewals', noun: 'firewall' },
  { kind: 'vmware', label: 'VMW renewals', noun: 'VMware licence' },
];

/* Anything due inside this many days needs someone to act now. */
export const SOON_DAYS = 90;

/* Autotask has no VMware type here, so a VMware licence is whatever asset
   names it — by type, product or its own name. */
const VMWARE = /vmware|\bvmw\b|esxi|vsphere|vcenter/i;

function kindOf(a: Asset): Kind | null {
  const text = [a.type, a.category, a.product, a.name].filter(Boolean).join(' ');
  if (VMWARE.test(text)) return 'vmware';
  if (/firewall/i.test(a.type ?? '') || /firewall|fortigate|sonicwall|watchguard|meraki mx/i.test(text)) return 'firewall';
  if (/server/i.test(a.type ?? '')) return 'server';
  return null;
}

export const shortDay = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function daysUntil(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${iso}T00:00:00`).getTime() - today.getTime()) / 86_400_000);
}

type State = 'expired' | 'soon' | 'ok' | 'unknown';
export const stateOf = (date: string | null): State =>
  date === null ? 'unknown' : daysUntil(date) < 0 ? 'expired' : daysUntil(date) <= SOON_DAYS ? 'soon' : 'ok';

/** Servers, firewalls and VMware from the configuration items, and the
 *  contracts that come up for renewal — the rows behind every dial. */
export function warrantyItems(assets: Asset[], contracts: Contract[]): Item[] {
  return [
    ...assets
      .filter((a) => a.active)
      .flatMap((a): Item[] => {
        const kind = kindOf(a);
        if (!kind) return [];
        return [
          {
            key: `a${a.id}`,
            kind,
            name: a.name,
            detail: [a.product, a.serialNumber].filter(Boolean).join(' · ') || null,
            date: a.warrantyExpires,
          },
        ];
      }),
    /* A contract with no end date (Time & Materials, say) never comes up for
       renewal, so it is not one to count. */
    ...contracts.filter((c) => c.end).map(
      (c): Item => ({
        key: `c${c.id}`,
        kind: 'contract',
        name: c.name,
        detail: [c.type, c.number].filter(Boolean).join(' · ') || null,
        date: c.end,
      }),
    ),
  ];
}

/** One dial: the share with more than SOON_DAYS to run, and the line under it. */
export function dialFor(items: Item[], kind: Kind, noun: string) {
  const mine = items.filter((i) => i.kind === kind);
  const dated = mine.filter((i) => i.date !== null);
  const expired = mine.filter((i) => stateOf(i.date) === 'expired').length;
  const soon = mine.filter((i) => stateOf(i.date) === 'soon').length;
  const ok = mine.filter((i) => stateOf(i.date) === 'ok').length;
  const undated = mine.length - dated.length;
  const hint =
    mine.length === 0
      ? `No ${noun} in Autotask`
      : [
          expired && `${expired} expired`,
          soon && `${soon} within ${SOON_DAYS} days`,
          !expired && !soon && `${mine.length} ${noun}${mine.length === 1 ? '' : 's'}, all current`,
          undated && `${undated} with no date`,
        ]
          .filter(Boolean)
          .join(' · ');
  return { mine, value: dated.length ? (ok / dated.length) * 100 : null, hint };
}

/** What the review deck keeps of a client's warranties (snapshot part "warranties"). */
export interface WarrantiesSnap {
  linked: boolean;
  companyName: string | null;
  items: Item[];
}

/** The same two Autotask reads as the client page's Warranties, for the deck. */
export async function loadWarranties(clientId: string, refresh: boolean): Promise<WarrantiesSnap> {
  const q = refresh ? '?refresh=1' : '';
  const [assets, services] = await Promise.all([
    api.get<{ linked: boolean; companyName: string | null; assets: Asset[] }>(`/am/clients/${clientId}/autotask/assets${q}`),
    api.get<{ linked: boolean; contracts: Contract[] }>(`/am/clients/${clientId}/autotask/services${q}`),
  ]);
  return {
    linked: assets.linked !== false && services.linked !== false,
    companyName: assets.companyName,
    items: warrantyItems(assets.assets ?? [], services.contracts ?? []),
  };
}

/**
 * A client's warranties and renewals, as four dials: server warranties,
 * contract renewals, firewall renewals and VMware renewals. Read live from
 * Autotask through the same two calls the client page's Assets and Services
 * tabs make (configuration items and active contracts), held until Refresh.
 *
 * Each dial is the share that needs nothing yet — more than 90 days to run —
 * against a target of all of them. A dial with nothing behind it is empty, not
 * a 0%: a client with no firewall is not a client whose firewall has lapsed.
 */
export function AmWarranties({ clientId, clientName }: { clientId: string; clientName: string }) {
  const assets = useHeldResource<{ linked: boolean; companyName: string | null; assets: Asset[] }>(
    `/am/clients/${clientId}/autotask/assets`,
  );
  const services = useHeldResource<{ linked: boolean; contracts: Contract[] }>(
    `/am/clients/${clientId}/autotask/services`,
  );
  const [focus, setFocus] = useState<Kind | null>(null);

  const loading = assets.loading || services.loading;
  const error = assets.error ?? services.error;
  const linked = assets.data?.linked !== false && services.data?.linked !== false;

  const items = warrantyItems(assets.data?.assets ?? [], services.data?.contracts ?? []);

  const shown = items
    .filter((i) => !focus || i.kind === focus)
    .sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999') || a.name.localeCompare(b.name));

  const refresh = () => {
    assets.refresh();
    services.refresh();
  };

  if (loading) return <Loading label="Reading warranties from Autotask" />;
  if (error) return <ErrorBanner message={error} />;
  if (!linked) {
    return (
      <Empty title="Not found in Autotask">
        <p className="muted">
          {clientName} could not be found in Autotask under its name here, so there is nothing to
          show. Importing it from Autotask (Account Management › Import from Autotask) matches the
          names.
        </p>
      </Empty>
    );
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <h3>Warranties</h3>
          <p className="sub">
            Server warranties, contract, firewall and VMware renewals for{' '}
            <strong>{assets.data?.companyName ?? clientName}</strong>, live from Autotask
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={assets.refreshing || services.refreshing}
          onClick={refresh}
        >
          {assets.refreshing || services.refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div className="card-body">
        <div className="gauge-grid mb-4">
          {KINDS.map(({ kind, label, noun }) => {
            const { mine, value, hint } = dialFor(items, kind, noun);
            return (
              <Gauge
                key={kind}
                label={label}
                value={value}
                target={100}
                hint={hint}
                onClick={mine.length ? () => setFocus(focus === kind ? null : kind) : undefined}
                drillLabel={focus === kind ? 'Show all' : 'Show these'}
              />
            );
          })}
        </div>

        <div className="row-between mb-2">
          <h3 style={{ margin: 0 }}>
            {focus ? KINDS.find((k) => k.kind === focus)?.label : 'Everything, soonest first'}
          </h3>
          {focus && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFocus(null)}>
              Show all
            </button>
          )}
        </div>

        {shown.length === 0 ? (
          <p className="muted small">Autotask has no servers, firewalls, VMware or contracts for this client.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ width: 140 }}>Kind</th>
                  <th style={{ width: 150 }}>Ends</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((i) => {
                  const state = stateOf(i.date);
                  const days = i.date ? daysUntil(i.date) : null;
                  return (
                    <tr key={i.key}>
                      <td>
                        <div className="strong">{i.name}</div>
                        {i.detail && <div className="xs muted">{i.detail}</div>}
                      </td>
                      <td className="xs">{KINDS.find((k) => k.kind === i.kind)?.label}</td>
                      <td className="xs">
                        {state === 'expired' ? (
                          <span className="badge badge-critical">{shortDay(i.date)}</span>
                        ) : (
                          shortDay(i.date)
                        )}
                        {days !== null && state === 'expired' && (
                          <div className="muted">expired {Math.abs(days)} days ago</div>
                        )}
                        {days !== null && state === 'soon' && <div className="muted">in {days} days</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
